import {
  ViewFolderId,
  ViewId,
} from "@redotech/effect-api-shared/ids/definitions";
import { useHandler } from "@redotech/react-util/hook";
import type { FilterGroup } from "@redotech/redo-model/views/views";
import { useSupportViewsFoldersCollections } from "../providers/support-views-folders-provider";

export function useSupportV2FolderActions(): {
  createFolder: (
    name: string,
    viewIds: string[],
    isPrivate: boolean,
  ) => Promise<void>;
  updateFolder: (
    folderId: string,
    name: string,
    viewIds: string[],
  ) => Promise<void>;
  deleteFolder: (folderId: string) => Promise<void>;
  createView: (
    name: string,
    filterGroup: FilterGroup | null,
    folderId: string | null,
  ) => Promise<string>;
  updateView: (
    viewId: string,
    name: string,
    filterGroup: FilterGroup | null,
  ) => Promise<void>;
  deleteView: (viewId: string) => Promise<void>;
} {
  const collections = useSupportViewsFoldersCollections();

  const createView = useHandler(
    async (
      name: string,
      filterGroup: FilterGroup | null,
      folderId: string | null,
    ): Promise<string> => {
      const viewId = ViewId.generate();
      collections.views.insert({
        id: viewId,
        name,
        table: "support-v2-conversations",
        filters: [],
        filterGroup,
        columns: [],
        sort: null,
        pageSize: null,
        folderId: folderId ? ViewFolderId.make(folderId) : null,
        position: 0,
        deleted: false,
        isNotEditable: false,
      });
      return String(viewId);
    },
  );

  const updateView = useHandler(
    async (
      viewId: string,
      name: string,
      filterGroup: FilterGroup | null,
    ): Promise<void> => {
      collections.views.update(ViewId.make(viewId), (draft) => {
        draft.name = name;
        draft.filterGroup = filterGroup;
      });
    },
  );

  const deleteView = useHandler(async (viewId: string): Promise<void> => {
    collections.views.delete(ViewId.make(viewId));
  });

  const createFolder = useHandler(
    async (name: string, _viewIds: string[], isPrivate: boolean) => {
      const folderId = ViewFolderId.generate();
      collections.folders.insert({
        id: folderId,
        name,
        table: "support-v2-conversations",
        position: 0,
        isPrivate,
      });
    },
  );

  const updateFolder = useHandler(
    async (folderId: string, name: string, _viewIds: string[]) => {
      collections.folders.update(ViewFolderId.make(folderId), (draft) => {
        draft.name = name;
      });
    },
  );

  const deleteFolder = useHandler(async (folderId: string) => {
    collections.folders.delete(ViewFolderId.make(folderId));
  });

  return {
    createView,
    updateView,
    deleteView,
    createFolder,
    updateFolder,
    deleteFolder,
  };
}
